const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * A duration as an operator says it out loud: "1h 47m", "12m", "40s".
 *
 * Two units at most. The third one is never the reason anybody does anything,
 * and it makes the column wider than the screen it has to share.
 */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${String(minutes % 60).padStart(2, "0")}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

/** "3m ago" / "in 12m". Null when there is no instant to describe. */
export function formatRelative(at: Date | null, now: Date): string {
  if (at === null) return "never";
  const delta = at.getTime() - now.getTime();
  const text = formatDuration(Math.abs(delta));
  return delta >= 0 ? `in ${text}` : `${text} ago`;
}

/**
 * The wall clock, local, to the minute.
 *
 * Separate from `formatStamp` because the two answer different questions.
 * This one sits next to a heading and says "the numbers under here are from
 * 21:03"; seconds there are noise on a panel that repolls every fifteen.
 */
export function formatClock(at: Date): string {
  return at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
}

/**
 * An absolute timestamp, local, to the second.
 *
 * Relative times answer "is this recent"; this answers "was this the same
 * moment as the thing in the other window", which is the question being asked
 * once something has actually gone wrong.
 */
export function formatStamp(at: Date): string {
  const sameDay = Math.abs(Date.now() - at.getTime()) < DAY;
  return at.toLocaleString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    ...(sameDay ? {} : { month: "short", day: "numeric" }),
  });
}
